import React, { FC, useCallback, useMemo } from 'react'

import FormikField from '../../../form/FormikField'
import { DeclarationFormLayoutProps } from '../../../../layouts/DeclarationFormLayout'
import { UnderlineTitleProps } from '../../../UnderlineTitle'
import { FormSubmitButtonProps } from '../../../form/FormSubmitButton'
import { FormFieldToggleButtonGroupProps } from '../../../form/fields/FormFieldToggleButtonGroup'
import { FormFieldInputProps } from '../../../form/fields/FormFieldInput'
import { FormErrorsProps } from '../../../form/FormErrors'
import { FormikFormProps } from '../../../form/FormikForm'
import { FormFieldSelectProps } from '../../../form/fields/FormFieldSelect'
import { DeclarationEntryProps } from '../../../DeclarationEntry'
import { DeclarationTableProps } from '../../../DeclarationTable'
import { ActionButtonProps } from '../../../ActionButton'
import { DeclarationBlockProps } from '../../../DeclarationBlock'
import { DeclarationAddMemberProps } from '../../../DeclarationAddMember'
import { DeclarationMemberListProps } from '../../../DeclarationMemberList'
import useIsMobile from '../../../../hooks/useIsMobile'
import { TextProps } from '../../../Text'
import { HtmlProps } from '../../../Html'
import DeleteModal, { DeleteModalProps } from '../../../DeleteModal'
import Modal, { ModalProps } from '../../../Modal'

import * as SC from './styled'

type Field = {
  field:
    | FormFieldToggleButtonGroupProps
    | FormFieldInputProps
    | FormFieldSelectProps
    | ActionButtonProps
    | DeclarationEntryProps
    | DeclarationTableProps
    | DeclarationBlockProps
    | DeclarationAddMemberProps
    | DeclarationMemberListProps
    | TextProps
  type:
    | 'toggle'
    | 'input'
    | 'select'
    | 'button'
    | 'declarationEntry'
    | 'declarationTable'
    | 'declarationBlock'
    | 'declarationAddMember'
    | 'declarationMemberList'
    | 'centeredText'
}

type Values = {
  [key: string]: any
}

type FormStep = {
  stepPart: UnderlineTitleProps
  label?: string
  text?: string
  html?: HtmlProps
  form?: FormikFormProps<Values>
  fields: Field[]
  button?: FormSubmitButtonProps
  confirmationButtons?: {
    modify?: ActionButtonProps
    confirm?: ActionButtonProps
  }
  modalProps?: ModalProps
  deleteModal?: DeleteModalProps
  errors?: FormErrorsProps['errors']
}

export type FullFormProps = {
  className?: string
  formLayout?: DeclarationFormLayoutProps
  formSteps: FormStep[]
  step: number
  modalProps?: ModalProps
  deleteModal?: DeleteModalProps
}

const FullForm: FC<FullFormProps> = (props) => {
  const { className, formLayout, formSteps, step } = props
  const isMobile = useIsMobile()

  const currentStep = useMemo(() => formSteps[step - 1], [formSteps, step])

  const renderField = useCallback(
    (field: Field, index: number): React.ReactNode => {
      if (field.type === 'input') {
        return <FormikField key={index} {...field.field} Component={SC.Input} />
      } else if (field.type === 'toggle') {
        if (isMobile) {
          return <FormikField key={index} {...field.field} Component={SC.Select} />
        } else {
          return <FormikField key={index} {...field.field} Component={SC.ToggleGroup} />
        }
      } else if (field.type === 'declarationEntry') {
        return <SC.DeclarationEntry key={index} {...(field.field as DeclarationEntryProps)} />
      } else if (field.type === 'declarationTable') {
        return <SC.DeclarationTable key={index} {...(field.field as DeclarationTableProps)} />
      } else if (field.type === 'button') {
        return <SC.Button key={index} {...(field.field as ActionButtonProps)} />
      } else if (field.type === 'declarationBlock') {
        return <SC.DeclarationBlock key={index} {...(field.field as DeclarationBlockProps)} />
      } else if (field.type === 'declarationAddMember') {
        return (
          <SC.DeclarationAddMember key={index} {...(field.field as DeclarationAddMemberProps)} />
        )
      } else if (field.type === 'declarationMemberList') {
        return (
          <SC.DeclarationMemberList key={index} {...(field.field as DeclarationMemberListProps)} />
        )
      } else if (field.type === 'centeredText') {
        return <SC.CenteredText key={index} {...(field.field as TextProps)} />
      }
    },
    [isMobile]
  )

  const renderStep = useCallback((): React.ReactNode => {
    const {
      stepPart,
      label,
      text,
      html,
      form,
      fields,
      button,
      confirmationButtons,
      errors,
      modalProps,
      deleteModal,
    } = currentStep
    return (
      form && (
        <>
          <SC.Step {...form} key={step}>
            {({ isValid, dirty }) => {
              const isSubmitDisabled =
                button?.disabled !== undefined ? button.disabled : !isValid || !dirty

              return (
                <>
                  <SC.StepHeader>
                    <SC.StepPart {...stepPart} />
                    {button && <SC.StepButton {...button} disabled={isSubmitDisabled} />}
                  </SC.StepHeader>
                  {label && <SC.StepLabel>{label}</SC.StepLabel>}
                  {text && <SC.StepText>{text}</SC.StepText>}
                  <SC.StepHtml {...html} />
                  {fields && (
                    <SC.StepFields>
                      {fields.map((field, index) => renderField(field, index))}
                    </SC.StepFields>
                  )}
                  {errors && <SC.StepErrors errors={errors} />}
                  {button && <SC.StepButton $isMobile {...button} disabled={isSubmitDisabled} />}
                  {confirmationButtons && (
                    <SC.WrapperButtons>
                      <SC.ConfirmationButton {...confirmationButtons.modify} />
                      <SC.ConfirmationButton {...confirmationButtons.confirm} />
                    </SC.WrapperButtons>
                  )}
                </>
              )
            }}
          </SC.Step>
          <Modal {...modalProps}>{deleteModal && <DeleteModal {...deleteModal} />}</Modal>
        </>
      )
    )
  }, [currentStep, renderField, step])

  return (
    <SC.Container className={className}>
      <SC.FormLayout {...formLayout}>{renderStep()}</SC.FormLayout>
    </SC.Container>
  )
}

export default FullForm
